import axios, { type AxiosError, type AxiosInstance } from "axios";
import { isLtiSessionExpired, type AuthService } from "@libertify/auth/core";

export type CustomAxiosError = AxiosError & {
  statusCode: number;
  message: string;
};

export interface SetupInterceptorsOptions {
  onMaintenanceError: (error: CustomAxiosError) => void;
  onAuthenticationFailure?: () => void;
  /** Called on a 401 when the LTI (Moodle) access token has expired. */
  onLtiSessionExpired?: () => void;
}

export interface CreateAdminAxiosConfig {
  service: AuthService;
  apiUrl: string;
  maintenanceStatus?: number;
  maxConcurrentRequests?: number;
  rateLimitIntervalMs?: number;
}

export interface AdminAxios {
  instance: AxiosInstance;
  setupInterceptors: (opts: SetupInterceptorsOptions) => void;
  resetRefreshState: () => void;
  resetPendingRequests: () => void;
}

/**
 * This extends the Axios error object with HttpError properties
 * so simple-rest can correctly handle the error.
 */
function adaptAxiosError(error: AxiosError): CustomAxiosError {
  // error.response is undefined when there's no response, such as Network Errors, Timeout, CORS error, Request cancelled
  if (!error.response) {
    const request: XMLHttpRequest = error.request;
    return {
      ...error,
      statusCode: request?.status ?? 500,
      message: error.message || "Network error or no response",
    };
  }

  const data: { detail?: string | { message: string } } = error.response
    .data as { detail?: string | { message: string } };
  const status = error.response.status;

  return {
    ...error,
    statusCode: status,
    message:
      typeof data?.detail === "string"
        ? data?.detail
        : (data?.detail?.message ?? ""),
  };
}

export const createAdminAxios = (config: CreateAdminAxiosConfig): AdminAxios => {
  const {
    service,
    apiUrl,
    maintenanceStatus = 503,
    maxConcurrentRequests = 10,
    rateLimitIntervalMs = 100,
  } = config;

  let pendingRequests = 0;
  let interceptorsInitialized = false;

  const instance = axios.create({
    baseURL: apiUrl,
    headers: {
      "Content-Type": "application/json",
    },
  });

  // Request interceptor: inject Authorization header
  instance.interceptors.request.use((requestConfig) => {
    const authHeader = service.getAuthorizationHeader();
    if (authHeader) {
      requestConfig.headers.Authorization = authHeader;
    }
    return requestConfig;
  });

  // execute in reverse order (LIFO - Last In, First Out)
  // for both successful responses and errors.
  const setupInterceptors = ({
    onMaintenanceError,
    onAuthenticationFailure,
    onLtiSessionExpired,
  }: SetupInterceptorsOptions) => {
    if (interceptorsInitialized) {
      return;
    }
    interceptorsInitialized = true;

    // Response interceptor for API calls
    // On 401: refresh the token but DON'T retry here.
    // React Query will retry automatically with the fresh token.
    // This avoids interceptor chain issues where retry response gets corrupted.
    instance.interceptors.response.use(
      (response) => response,
      async function (error) {
        const originalRequest = error.config;

        // Only attempt refresh once per request
        if (originalRequest?._refreshAttempted) {
          throw error;
        }

        if (error?.response?.status === 401) {
          originalRequest._refreshAttempted = true;

          // LTI tokens can't be refreshed — an expired one means the session is over.
          if (isLtiSessionExpired(service.getToken())) {
            onLtiSessionExpired?.();
            throw error;
          }

          try {
            const accessToken = await service.refreshToken();

            // Refresh produced no new token. Only treat this as an auth
            // failure if there's also no usable token left. Sessions that
            // legitimately have no refresh_token (e.g. LTI transfer-token
            // logins, provider "cognito") return null here while still holding
            // a valid access token — logging them out would break the retry
            // path below. A first-launch 401 for these is typically a
            // transient race right after the transfer-token exchange, which a
            // plain retry clears.
            if (!accessToken && !service.isAuthenticated()) {
              onAuthenticationFailure?.();
              throw error;
            }

            // Either the token was refreshed, or refresh was a no-op but a
            // valid token is still present. Reject with the original error and
            // let React Query retry — the request interceptor re-attaches the
            // current token on the next attempt.
            throw error;
          } catch (refreshError) {
            // If refresh itself failed, trigger logout
            if (refreshError !== error) {
              onAuthenticationFailure?.();
            }
            throw error;
          }
        }

        throw error;
      }
    );

    // Handle maintenance mode
    instance.interceptors.response.use(
      (response) => response,
      function (error: AxiosError) {
        if (error.response?.status === maintenanceStatus) {
          onMaintenanceError(adaptAxiosError(error));
          // return resolve promise to avoid the refine error notification,
          // with empty data. Without resolve, it also shows a notification
          // with no data to destructure.
          return Promise.resolve({});
        }
        return Promise.reject(error);
      }
    );

    // transforms the error to a custom error object
    instance.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => Promise.reject(adaptAxiosError(error))
    );
  };

  // careful: these rate-limit interceptors are not working when nested in
  // setupInterceptors and would block execution of the requests.

  // Request interceptor: rate limiting (queues when too many in-flight)
  instance.interceptors.request.use(
    (requestConfig) =>
      new Promise((resolve) => {
        const interval = globalThis.window.setInterval(() => {
          if (pendingRequests < maxConcurrentRequests) {
            pendingRequests++;
            clearInterval(interval);
            resolve(requestConfig);
          }
        }, rateLimitIntervalMs);
      }),
    () => Promise.reject(new Error("Request rejected"))
  );

  // Response interceptor: rate limiting (decrement on completion).
  // Forward the error untouched — the transform interceptor inside
  // setupInterceptors already runs `adaptAxiosError` on its way through.
  instance.interceptors.response.use(
    (response) => {
      pendingRequests = Math.max(0, pendingRequests - 1);
      return response;
    },
    (error) => {
      pendingRequests = Math.max(0, pendingRequests - 1);
      return Promise.reject(error);
    }
  );

  const resetRefreshState = (): void => {
    service.resetRefreshState();
  };

  const resetPendingRequests = (): void => {
    pendingRequests = 0;
  };

  return { instance, setupInterceptors, resetRefreshState, resetPendingRequests };
};
